// store/index.js
import { createStore } from 'vuex';

// setToken callers have historically dispatched three different payload shapes:
// the proper `{ token, userdata }` object (Login.doLogin), a bare token string
// (main.js boot), and `{ token, userdata }` where userdata is still the raw JSON
// string straight out of localStorage (Login.created auto-login). The mutation
// used to destructure `{ token, user }` — a field no caller ever sent — so
// `getUserdata`/`getUser` were `undefined` on every path and every component had
// to read localStorage instead.
//
// Normalizing here instead of trusting the shape means the getters return a real
// object no matter which path logged the user in, and that a malformed payload
// can never throw: `setToken` runs in main.js at boot, BEFORE `mount()`, so a
// TypeError there is a white screen with no login form to recover from.
function normalizeAuth(payload) {
  const p = typeof payload === 'string' ? { token: payload } : (payload || {});
  let userdata = p.userdata;
  if (typeof userdata === 'string') {
    try { userdata = JSON.parse(userdata); } catch (e) { userdata = null; }
  }
  return { token: p.token || null, userdata: userdata || null };
}

const store = createStore({
  state: {
    // `user` and `userdata` are NOT two different things — they always hold the
    // same object. `getUser` came first and `getUserdata` was added next to it;
    // both are kept because callers exist for the latter.
    user: null,
    token: null,
    userdata: null,
  },
  mutations: {
    setToken(state, payload) {
      const { token, userdata } = normalizeAuth(payload);
      state.token = token;
      state.user = userdata;
      state.userdata = userdata;
    },
    clearToken(state) {
      state.token = null;
      state.user = null;  // Clear user information when logging out
      state.userdata = null;
    },
  },
  actions: {
    // payload = { token, userdata } — see normalizeAuth for the shapes it tolerates.
    setToken({ commit }, payload) {
      commit('setToken', payload);
    },
    clearToken({ commit }) {
      commit('clearToken');
    },
    logout({ commit }) {
      commit('clearToken');
    },
  },
  getters: {
    // ⚠️ token ONLY — this does NOT mean `getUserdata` is populated. The
    // half-state (token present, userdata missing) is the one that deadlocked
    // prod, so don't read this as "the user is fully loaded"; check the value
    // you actually need. The old comment here claimed it checked both.
    isLoggedIn: state => !!state.token,
    getToken: (state) => state.token,
    getUser: (state) => state.user,
    getUserdata: (state) => state.userdata
  },
});

export default store;
